import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from "moment";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import {
  Modal,
} from "react-bootstrap";
import swal from "sweetalert";
import axiosInstance from '../../services/AxiosInstance';
import '../tasks/tasklist.css';
import '../Documents/commonstyle.css';
import './projectlist.css';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import { hasFeatureAccess } from '../../utils/featureLock';
import { isCorporateAdmin, getCurrentUserRole } from '../../utils/roleAccess';
import CommonTable from '../components/CommonTable/CommonTable';
import { usePageToolbar } from '../layouts/nav/PageToolbarContext';
import {
  getProjectStatusOptions,
  getProjectStatusLabel,
  PROJECT_STATUS,
} from '../../utils/projectStatus';
import { buildQuerySuffix } from '../../utils/listQueryPersistence';

const ProjectList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [searchText, setSearchText] = useState(() => searchParams.get('search') || '');
  const [searchDropDown, setSearchDropDown] = useState(false)
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
    Privacy: searchParams.get('Privacy') || '',
  });
  const filterUrlSyncSkipRef = useRef(false);
  const [error, setError] = useState()
  const [loading, setLoading] = useState(true)
  const [hasProjectAccess, setHasProjectAccess] = useState(false)
  const [featureLoading, setFeatureLoading] = useState(true)
  const statusMap = {
    'Pending': PROJECT_STATUS.PENDING,
    'In Progress': PROJECT_STATUS.IN_PROGRESS,
    'Mark as Completed': PROJECT_STATUS.MARK_AS_COMPLETED,
    'Completed': PROJECT_STATUS.COMPLETED,
  };
  
  const filterStatusOptions = getProjectStatusOptions({ isCreator: true });
  const privacyMap = {
    Public: 0,
    Private: 1,
    Custom: 2
  };
  const filterRef = useRef(null);
  const optionsRef = useRef(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [optionsDropdownOpen, setOptionsDropdownOpen] = useState(false);
  const [transactionStatementDates, setTransactionStatementDates] = useState({
    startDate: moment().toDate(),
    endDate: moment().toDate(),
  });
  const [exportingTransactionStatement, setExportingTransactionStatement] = useState(false);
  const [productivityReportDates, setProductivityReportDates] = useState({
    startDate: moment().toDate(),
    endDate: moment().toDate(),
  });
  const [exportingProductivityReport, setExportingProductivityReport] = useState(false);
  const [exportingProjectList, setExportingProjectList] = useState(false);
  
  const userRole = getCurrentUserRole();
  const isCorporateAdminUser = isCorporateAdmin(userRole);

  // Helper function to get current user data
  const getCurrentUser = () => {
    const userData = localStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  };

  // Helper function to check if current user is the project creator
  const isProjectCreator = (project) => {
    const currentUser = getCurrentUser();
    return currentUser && project.created_by === currentUser.id;
  };

  // Helper function to check if current user is a project member
  const isProjectMember = (project) => {
    const currentUser = getCurrentUser();
    if (!currentUser) return false;
    
    // Check if user is in ProjectMembers array
    return project.ProjectMembers && project.ProjectMembers.some(
      member => member.CorporateUser && member.CorporateUser.id === currentUser.id
    );
  };

  // Helper function to check if current user is a project moderator
  const isProjectModerator = (project) => {
    const currentUser = getCurrentUser();
    if (!currentUser) return false;
    
    // Check if user is in ProjectModerators array
    return project.ProjectModerators && project.ProjectModerators.some(
      moderator => moderator.CorporateUser && moderator.CorporateUser.id === currentUser.id
    );
  };

  // Helper function to check if current user can edit/delete (creator or moderator)
  const canEditOrDelete = (project) => {
    return isProjectCreator(project) || isProjectModerator(project);
  };

  // Check project access on component mount
  useEffect(() => {
    const checkProjectAccess = async () => {
      try {
        setFeatureLoading(true);
        const projectAccess = await hasFeatureAccess('project');
        setHasProjectAccess(projectAccess);
        
        if (!projectAccess) {
          setFeatureLoading(false);
          return;
        }
        
        // Only fetch projects if user has access
        await handleSearch();
      } catch (error) {
        console.error('Error checking project access:', error);
        setHasProjectAccess(false);
      } finally {
        setFeatureLoading(false);
      }
    };
    
    checkProjectAccess();
  }, []);

  // Helper function to check if current user can change status (creator, moderator, or member).
  // Re-opening a completed project is creator-only.
  const canChangeStatus = (project) => {
    if (Number(project?.status) === PROJECT_STATUS.COMPLETED) {
      return isProjectCreator(project);
    }
    return isProjectCreator(project) || isProjectModerator(project) || isProjectMember(project);
  };

  // Helper function to check if current user has any access to the project
  const hasProjectPermission = (project) => {
    return isProjectCreator(project) || isProjectModerator(project) || isProjectMember(project);
  };

  useEffect(() => {
    handleSearch()
  }, []);


  const handleDeleteWithConfirmation = async (id) => {
    const willDelete = await swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this project and all related tasks!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (willDelete) {
      try {
        projectTasks(id);
      } catch (err) {
        notifyError("An unexpected error occurred");
        console.error(err);
      }
    }
  };

  const handleDelete = async (projectid) => {
    try {
      const response = await axiosInstance.delete(`/project/delete/${projectid}`)
      const data = response.data;
      console.log(data)
      if (data.message == 'Project deleted successfully') {
        setProjects(projects.filter((p) => p.id !== projectid));
        return 'deleted';
      }
      else {
        notifyError(data.message)
      }
    } catch (error) {
      console.error('Delete error:', error);
      notifyError(error)
    }
  };

  const projectTasks = (projectid) => {
    // Fetch tasks for the specific project
    axiosInstance.get(`/project/tasks/${projectid}`)
      .then(async response => {
        const tasks = response.data.data || [];
        if (tasks.length > 0) {
          const willDelete = await swal({
            title: "Are you sure?",
            text: "The Project has tasks associated with it. Deleting it will also remove all related tasks.",
            icon: "warning",
            buttons: true,
            dangerMode: true,
          });
          if (willDelete) {
            const result = await handleDelete(projectid);
            if (result === 'deleted') {
              swal("Deleted successfully!", {
                icon: "success",
              });
            } else {
              notifyError("Error in deleting");
            }
          }
        } else {
          const result = await handleDelete(projectid);
          if (result === 'deleted') {
            swal("Deleted successfully!", {
              icon: "success",
            });
          } else {
            notifyError("Error in deleting");
          }
        }
      })
      .catch(error => {
        console.error('Error fetching project tasks:', error);
      });
  }



  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString();
  };

  const isCompleted = (row) => row.status === 3;

  const getProgressLabel = (row) => {
    const endDate = row.end_date ? new Date(row.end_date) : null;
    if (!endDate || isNaN(endDate.getTime())) return '—';
    endDate.setHours(0, 0, 0, 0);

    if (isCompleted(row) && row.actual_completion_date) {
      const actual = new Date(row.actual_completion_date);
      actual.setHours(0, 0, 0, 0);
      const diffMs = actual - endDate;
      const diffDays = Math.floor(Math.abs(diffMs) / (24 * 60 * 60 * 1000));
      if (diffMs === 0) return 'Completed on time';
      if (diffMs > 0) return diffDays === 1 ? '1 day delay' : `${diffDays} days delay`;
      return diffDays === 1 ? 'Completed 1 day early' : `Completed ${diffDays} days early`;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffMs = endDate - today;
    const diffDays = Math.floor(Math.abs(diffMs) / (24 * 60 * 60 * 1000));
    if (diffMs === 0) return 'Due today';
    if (diffMs < 0) return diffDays === 1 ? '1 day delay' : `${diffDays} days delay`;
    return diffDays === 1 ? 'Will complete in 1 day' : `Will complete in ${diffDays} days`;
  };

  const getProgressStyle = (row) => {
    const endDate = row.end_date ? new Date(row.end_date) : null;
    if (!endDate || isNaN(endDate.getTime())) return '';
    endDate.setHours(0, 0, 0, 0);
    if (isCompleted(row) && row.actual_completion_date) {
      const actual = new Date(row.actual_completion_date);
      actual.setHours(0, 0, 0, 0);
      if (actual > endDate) return 'text-danger fw-medium';
      if (actual < endDate) return 'text-success fw-medium';
      return '';
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return endDate < today ? 'text-danger fw-medium' : '';
  };


  const getProjectListQuerySuffix = () => buildQuerySuffix({
    search: searchText,
    status: filters.status,
    startDate: filters.startDate,
    endDate: filters.endDate,
    Privacy: filters.Privacy,
    ...(searchParams.get('delayed') === '1' ? { delayed: '1' } : {}),
  });

  const commitProjectListToUrl = (applied) => {
    const activeSearch = applied?.searchText ?? searchText;
    const activeFilters = applied?.filters ?? filters;
    const keepDelayed = applied?.clearDelayed
      ? false
      : searchParams.get('delayed') === '1';
    filterUrlSyncSkipRef.current = true;
    setSearchParams(() => {
      const p = new URLSearchParams();
      if (activeSearch) p.set('search', activeSearch);
      if (activeFilters.status) p.set('status', activeFilters.status);
      if (activeFilters.startDate) p.set('startDate', activeFilters.startDate);
      if (activeFilters.endDate) p.set('endDate', activeFilters.endDate);
      if (activeFilters.Privacy) p.set('Privacy', activeFilters.Privacy);
      if (keepDelayed) p.set('delayed', '1');
      return p;
    }, { replace: true });
  };

  const isDelayedProject = (project) => {
    if (!project?.end_date) return false;
    if (Number(project.status) === 3) return false;
    const endDate = new Date(project.end_date);
    if (Number.isNaN(endDate.getTime())) return false;
    endDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return endDate < today;
  };

  const delayedOnly = searchParams.get('delayed') === '1';
  const visibleProjects = useMemo(
    () => (delayedOnly ? projects.filter(isDelayedProject) : projects),
    [projects, delayedOnly]
  );

  const handleEdit = (projectid) => {
    navigate(`/task/editproject${getProjectListQuerySuffix()}`, {
      state: { projectid },
    });
  };

  const handleChangeStatus = (project) => {
    setSelectedProject(project);
    setNewStatus(project.status);
    setShowStatusModal(true);
  };

  const handleStatusChange = async () => {
    if (!selectedProject || !newStatus) return;

    setStatusLoading(true);
    try {
      const response = await axiosInstance.put(`/project/change-status/${selectedProject.id}`, {
        status: parseInt(newStatus)
      });

      if (response.data) {
        // Update the project in the list
        setProjects(projects.map(project => 
          project.id === selectedProject.id 
            ? { ...project, status: parseInt(newStatus) }
            : project
        ));
        
        toast.success("Project status updated successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        
        setShowStatusModal(false);
        setSelectedProject(null);
        setNewStatus('');
      }
    } catch (error) {
      console.error('Error updating project status:', error);
      notifyError(error.response?.data?.message || 'Failed to update project status');
    } finally {
      setStatusLoading(false);
    }
  };

  const getStatusLabel = (status) => getProjectStatusLabel(status);

  const notifyError = (data) => {
    toast.error(`❌ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };
  const svg1 = (
    <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <rect x="0" y="0" width="24" height="24"></rect>
        <circle fill="#000000" cx="5" cy="12" r="2"></circle>
        <circle fill="#000000" cx="12" cy="12" r="2"></circle>
        <circle fill="#000000" cx="19" cy="12" r="2"></circle>
      </g>
    </svg>
  );
  const handleClickOutside = (event) => {
    if (filterRef.current && !filterRef.current.contains(event.target)) {
      setSearchDropDown(false); // close only — do not wipe filters / list
    }
    // Close Options dropdown when clicking outside (including outside daterangepicker)
    const clickedInDatePicker = event.target.closest?.('.daterangepicker');
    const clickedInOptions = event.target.closest?.('.options-wrapper') || event.target.closest('.options-toggle-btn');
    if (optionsDropdownOpen && !clickedInDatePicker && !clickedInOptions) {
      setOptionsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [optionsDropdownOpen]);

  const handleSearch = async (pageNum = 1, append = false, override = null) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const activeFilters = override?.filters || filters;
      const activeSearch = override?.searchText ?? searchText;

      const params = new URLSearchParams({
        page: pageNum,
        limit: 10
      });

      const numericStatus = statusMap[activeFilters.status] ?? '';
      const userData = JSON.parse(localStorage.getItem("user"));

      if (numericStatus !== '') params.append('Status', numericStatus);
      const apiStartDate = toApiFormat(activeFilters.startDate);
      const apiEndDate = toApiFormat(activeFilters.endDate);
      if (apiStartDate) params.append('StartDate', apiStartDate);
      if (apiEndDate) params.append('EndDate', apiEndDate);
      if (activeFilters.Privacy) params.append('Privacy', privacyMap[activeFilters.Privacy]);
      if (activeSearch) params.append('search', activeSearch);

      params.append('CorporateId', parseInt(userData.corporateId));

      const res = await axiosInstance.get(`/project/list?${params.toString()}`);
      const result = res.data.data || [];

      if (append) {
        setProjects(prev => [...prev, ...result]);
      } else {
        setProjects(result);
      }

      setHasMore(result.length === 10);
      setPage(pageNum);
      if (!append && !override?.skipCommit) {
        commitProjectListToUrl({ filters: activeFilters, searchText: activeSearch });
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (hasProjectAccess) {
      handleSearch()
    }
  }, [searchText, hasProjectAccess])

  // Restore filters when returning via browser history / Back with query
  useEffect(() => {
    if (filterUrlSyncSkipRef.current) {
      filterUrlSyncSkipRef.current = false;
      return;
    }
    const next = {
      status: searchParams.get('status') || '',
      startDate: searchParams.get('startDate') || '',
      endDate: searchParams.get('endDate') || '',
      Privacy: searchParams.get('Privacy') || '',
    };
    const nextSearch = searchParams.get('search') || '';
    setFilters(next);
    setSearchText(nextSearch);
    if (hasProjectAccess) {
      handleSearch(1, false, { filters: next, searchText: nextSearch, skipCommit: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const resetFilters = () => {
    const cleared = { status: '', startDate: '', endDate: '', Privacy: '' };
    setFilters(cleared);
    setSearchText('');
    // Clear URL; fetch with overrides so we don't depend on async state
    filterUrlSyncSkipRef.current = true;
    setSearchParams(new URLSearchParams(), { replace: true });
    // If URL was already empty, sync effect won't run — clear the skip flag
    queueMicrotask(() => {
      filterUrlSyncSkipRef.current = false;
    });
    void handleSearch(1, false, {
      filters: cleared,
      searchText: '',
      skipCommit: true,
    });
  };

  const formatDateForApi = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for API
  const toApiFormat = (ddMmYyyy) => {
    if (!ddMmYyyy || !/^\d{2}\/\d{2}\/\d{4}$/.test(ddMmYyyy.trim())) return '';
    const [dd, mm, yyyy] = ddMmYyyy.trim().split('/');
    const d = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
    if (isNaN(d.getTime())) return '';
    return formatDateForApi(d);
  };

  const handleExportTransactionStatement = async () => {
    const { startDate, endDate } = transactionStatementDates;
    if (!startDate || !endDate) {
      toast.error('Please select a date range');
      return;
    }
    const date_from = formatDateForApi(startDate);
    const date_to = formatDateForApi(endDate);
    if (new Date(date_from) > new Date(date_to)) {
      toast.error('Start date must be before or equal to end date');
      return;
    }
    try {
      setExportingTransactionStatement(true);
      const res = await axiosInstance.get(`/task/daily-updates/my-projects/export-xlsx?date_from=${encodeURIComponent(date_from)}&date_to=${encodeURIComponent(date_to)}`, {
        responseType: 'blob',
      });
      const contentType = res.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const data = JSON.parse(text);
        if (data?.success === false) {
          toast.error(data.message || 'No data to export');
          setOptionsDropdownOpen(false);
          return;
        }
      }
      const downloadUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `MyProjects_DailyUpdates_${date_from}_to_${date_to}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Transaction statement exported successfully');
      setOptionsDropdownOpen(false);
    } catch (err) {
      console.error('Export error:', err);
      const errMsg = err.response?.data instanceof Blob
        ? 'Export failed'
        : (err.response?.data?.message || err.message || 'Failed to export transaction statement');
      toast.error(errMsg);
    } finally {
      setExportingTransactionStatement(false);
    }
  };

  const handleExportProductivityReport = async () => {
    const { startDate, endDate } = productivityReportDates;
    if (!startDate || !endDate) {
      toast.error('Please select a date range');
      return;
    }
    const start_date = formatDateForApi(startDate);
    const end_date = formatDateForApi(endDate);
    if (new Date(start_date) > new Date(end_date)) {
      toast.error('Start date must be before or equal to end date');
      return;
    }
    try {
      setExportingProductivityReport(true);
      const res = await axiosInstance.get(`/task/reports/user-task-report?start_date=${encodeURIComponent(start_date)}&end_date=${encodeURIComponent(end_date)}`, {
        responseType: 'blob',
      });
      const downloadUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `UserTaskReport_${start_date}_to_${end_date}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Productivity report exported successfully');
      setOptionsDropdownOpen(false);
    } catch (err) {
      console.error('Export error:', err);
      const errMsg = err.response?.data instanceof Blob
        ? 'Export failed'
        : (err.response?.data?.message || err.message || 'Failed to export productivity report');
      toast.error(errMsg);
    } finally {
      setExportingProductivityReport(false);
    }
  };

  const handleExportProjectList = async () => {
    try {
      setExportingProjectList(true);
      const res = await axiosInstance.get('/project/reports/project-list', {
        responseType: 'blob',
      });
      const contentType = res.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const data = JSON.parse(text);
        if (data?.success === false) {
          toast.error(data.message || 'No data to export');
          setOptionsDropdownOpen(false);
          return;
        }
      }
      const downloadUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `ProjectList_${moment().format('YYYY-MM-DD')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Project list exported successfully');
      setOptionsDropdownOpen(false);
    } catch (err) {
      console.error('Export error:', err);
      const errMsg = err.response?.data instanceof Blob
        ? 'Export failed'
        : (err.response?.data?.message || err.message || 'Failed to export project list');
      toast.error(errMsg);
    } finally {
      setExportingProjectList(false);
    }
  };

  useEffect(() => {
    const handleWindowScroll = () => {
      const threshold = 5;
      if (
        window.innerHeight + window.scrollY >= document.body.offsetHeight - threshold &&
        hasMore && !loadingMore && !loading
      ) {
        handleSearch(page + 1, true);
      }
    };
    window.addEventListener('scroll', handleWindowScroll);
    return () => {
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [hasMore, loadingMore, loading, page]);

  const projectToolbar = useMemo(
    () => {
      if (featureLoading || !hasProjectAccess) return null;
      return (
        <>
          <button
            type="button"
            className="options-toggle-btn reports-btn header-toolbar-btn"
            onClick={() => setOptionsDropdownOpen((open) => !open)}
            title="Reports"
            aria-label="Reports"
          >
            <i className="fas fa-chart-bar"></i>
          </button>
        </>
      );
    },
    [featureLoading, hasProjectAccess]
  );

  usePageToolbar(projectToolbar, [projectToolbar]);

  // Show loading screen while checking feature access
  if (featureLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Show feature locked message if user doesn't have project access
  if (!hasProjectAccess) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center">
                  <div className="feature-locked">
                    <i className="fas fa-lock" style={{ fontSize: '3rem', marginBottom: '1rem', color: '#c42126' }} aria-hidden="true"></i>
                    <h4 className="text-muted mb-3">Project Management Not Available</h4>
                    <p className="text-muted mb-4">
                      Project management is not available in your current subscription plan.
                      Upgrade your plan to access this feature.
                    </p>
                    <div className="d-flex flex-wrap gap-3 justify-content-center align-items-center">
                      <button
                        type="button"
                        className="btn doc-form-cancel-btn"
                        onClick={() => navigate('/task/tasklist')}
                      >
                        <i className="fas fa-arrow-left me-2" aria-hidden="true"></i>
                        Back to Task List
                      </button>
                      <button
                        type="button"
                        className="btn doc-form-submit-btn"
                        onClick={() => navigate('/user-subscription')}
                      >
                        <i className="fas fa-arrow-right me-2" aria-hidden="true"></i>
                        Upgrade Plan
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='list-row'>
      <ToastContainer />
      {optionsDropdownOpen && (
              <div className="options-wrapper filter-wrapper" ref={optionsRef}>
                <div className="content options-content-row">
                  {/* Part 1: Transaction Statement */}
                  <div className="options-section">
                    <h6 className="mb-3 text-label">Transaction Statement</h6>
                    <div className="row">
                      <div className="col-12 mb-2">
                        <label className="text-label d-block mb-1">Date Range</label>
                        <DateRangePicker
                          initialSettings={{
                            ...(transactionStatementDates.startDate && transactionStatementDates.endDate ? {
                              startDate: moment(transactionStatementDates.startDate),
                              endDate: moment(transactionStatementDates.endDate),
                            } : {}),
                            locale: { format: 'DD/MM/YYYY' },
                            autoApply: true,
                          }}
                          onCallback={(start, end) => {
                            setTransactionStatementDates({
                              startDate: start.toDate(),
                              endDate: end.toDate(),
                            });
                          }}
                          onApply={(event, picker) => {
                            if (picker.startDate && picker.endDate) {
                              setTransactionStatementDates({
                                startDate: picker.startDate.toDate(),
                                endDate: picker.endDate.toDate(),
                              });
                            }
                          }}
                        >
                          <input
                            type="text"
                            className="form-control input-daterange-timepicker"
                            readOnly
                            placeholder="Select date range"
                          />
                        </DateRangePicker>
                      </div>
                      <div className="col-12 mb-2">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm d-flex align-items-center export-download-btn"
                          onClick={handleExportTransactionStatement}
                          disabled={exportingTransactionStatement || !transactionStatementDates.startDate || !transactionStatementDates.endDate}
                        >
                          {exportingTransactionStatement ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Exporting...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-download me-1"></i>
                              Download
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {isCorporateAdminUser && (
                    <>
                      <div className="options-divider-vertical" aria-hidden="true" />
                      {/* Part 2: Productivity Report - corporate admin only */}
                      <div className="options-section">
                        <h6 className="mb-3 text-label">Productivity Report</h6>
                        <div className="row">
                          <div className="col-12 mb-2">
                            <label className="text-label d-block mb-1">Date Range</label>
                            <DateRangePicker
                              initialSettings={{
                                ...(productivityReportDates.startDate && productivityReportDates.endDate ? {
                                  startDate: moment(productivityReportDates.startDate),
                                  endDate: moment(productivityReportDates.endDate),
                                } : {}),
                                locale: { format: 'DD/MM/YYYY' },
                                autoApply: true,
                              }}
                              onCallback={(start, end) => {
                                setProductivityReportDates({
                                  startDate: start.toDate(),
                                  endDate: end.toDate(),
                                });
                              }}
                              onApply={(event, picker) => {
                                if (picker.startDate && picker.endDate) {
                                  setProductivityReportDates({
                                    startDate: picker.startDate.toDate(),
                                    endDate: picker.endDate.toDate(),
                                  });
                                }
                              }}
                            >
                              <input
                                type="text"
                                className="form-control input-daterange-timepicker"
                                readOnly
                                placeholder="Select date range"
                              />
                            </DateRangePicker>
                          </div>
                          <div className="col-12 mb-2">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm d-flex align-items-center export-download-btn"
                              onClick={handleExportProductivityReport}
                              disabled={exportingProductivityReport || !productivityReportDates.startDate || !productivityReportDates.endDate}
                            >
                              {exportingProductivityReport ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="fa fa-download me-1"></i>
                                  Download
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="options-divider-vertical" aria-hidden="true" />

                  {/* Part 3: Project Report */}
                  <div className="options-section options-section-last">
                    <h6 className="mb-3 text-label">Project Report</h6>
                    <div className="row">
                      <div className="col-12 mb-2">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm d-flex align-items-center export-download-btn"
                          onClick={handleExportProjectList}
                          disabled={exportingProjectList}
                        >
                          {exportingProjectList ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Exporting...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-download me-1"></i>
                              Download
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {searchDropDown ? (
              <div className="filter-wrapper" ref={filterRef}>


                <div className="content">


                  <div className="main-filters">

                    <div className="row">
                      <div className="col-lg-6 mb-2">
                        <label className="text-label">Privacy</label>
                        <select
                          className="form-control"
                          name="Privacy"
                          value={filters.Privacy}
                          onChange={handleChange}
                        >
                          <option value="">Select</option>
                          <option>Public</option>
                          <option>Private</option>
                        </select>
                      </div>
                      <div className="col-lg-6 mb-2">
                        <label className="text-label">Status</label>
                        <select
                          className="form-control"
                          name="status"
                          value={filters.status}
                          onChange={handleChange}
                        >
                          <option value="">Select</option>
                          {filterStatusOptions.map((option) => (
                            <option key={option.value} value={option.label}>
                              {option.label}
                            </option>
                          ))}
                        </select>

                      </div>
                    </div>
                    <div className='row'>
                      <div className="col-lg-6 mb-2">
                        <label className="text-label">StartDate</label>
                        <input
                          type="text"
                          className="form-control"
                          name="startDate"
                          placeholder="DD/MM/YYYY"
                          value={filters.startDate}
                          onChange={handleChange}
                        />

                      </div>
                      <div className="col-lg-6 mb-2">
                        <label className="text-label">EndDate</label>
                        <input
                          type="text"
                          className="form-control"
                          name="endDate"
                          placeholder="DD/MM/YYYY"
                          value={filters.endDate}
                          onChange={handleChange}
                        />

                      </div>
                    </div>
                    <div className="actions">
                      <button className="search-btn" onClick={handleSearch}>Search</button>
                      <button className="reset-btn" onClick={resetFilters}>Reset</button>
                    </div>
                  </div>
                </div>
              </div>

            ) : (
              null
            )}

                <CommonTable
                  onRowClick={(row, rowIndex, e) => {
                    if (!e.target.closest('.actions-cell')) {
                      navigate(`/task/projectdetails/${row.id}${getProjectListQuerySuffix()}`);
                    }
                  }}
                  columns={[
                    {
                      key: 'index',
                      header: '#',
                      accessor: (row, index) => index + 1,
                      headerClassName: 'width80',
                    },
                    {
                      key: 'name',
                      header: 'Name',
                      render: (row) => (
                        <a 
                          href={`/task/projectdetails/${row.id}${getProjectListQuerySuffix()}`}
                          className="project-link"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/task/projectdetails/${row.id}${getProjectListQuerySuffix()}`);
                          }}
                        >
                          {row.name}
                        </a>
                      ),
                    },
                    {
                      key: 'owner',
                      header: 'Owner',
                      accessor: (row) => row?.owner?.full_name || '—',
                    },
                    {
                      key: 'startDate',
                      header: 'Start Date',
                      accessor: (row) => formatDate(row.start_date),
                    },
                    {
                      key: 'endDate',
                      header: 'End Date',
                      accessor: (row) => formatDate(row.end_date),
                    },
                    {
                      key: 'priority',
                      header: 'Priority',
                      accessor: (row) => row.priority || '—',
                    },
                    {
                      key: 'actualCompletionDate',
                      header: 'Actual Completion Date',
                      accessor: (row) => formatDate(row.actual_completion_date),
                    },
                    {
                      key: 'progress',
                      header: 'Progress',
                      accessor: (row) => getProgressLabel(row),
                      render: (row) => {
                        const label = getProgressLabel(row);
                        const className = getProgressStyle(row);
                        return <span className={className}>{label}</span>;
                      },
                    },
                    {
                      key: 'status',
                      header: 'Status',
                      accessor: (row) => getStatusLabel(row.status),
                    },
                  ]}
                  data={visibleProjects}
                  actions={(row) => {
                    const actions = [];
                    
                    if (canEditOrDelete(row)) {
                      actions.push({
                        label: 'Edit',
                        icon: 'fas fa-edit',
                        onClick: () => handleEdit(row.id),
                      });
                    }
                    
                    if (canChangeStatus(row)) {
                      actions.push({
                        label: 'Change Status',
                        icon: 'fas fa-exchange-alt',
                        onClick: () => handleChangeStatus(row),
                      });
                    }
                    
                    if (canEditOrDelete(row)) {
                      actions.push({
                        label: 'Delete',
                        icon: 'fas fa-trash',
                        className: 'text-danger',
                        onClick: () => handleDeleteWithConfirmation(row.id),
                      });
                    }
                    
                    return actions;
                  }}
                  emptyMessage="You don't have access to any projects yet."
                  emptyStateIcon="fas fa-folder-open"
                  getRowId={(row) => row.id}
                  maxHeight="75vh"
                  minHeight="280px"
                />
      
      {/* Change Status Modal */}
      <Modal 
        show={showStatusModal} 
        onHide={() => setShowStatusModal(false)} 
        centered
        className="status-change-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Project Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProject && (
            <div>
              <p><strong>Project:</strong> {selectedProject.name}</p>
              <p><strong>Current Status:</strong> {getStatusLabel(selectedProject.status)}</p>
              {!isProjectCreator(selectedProject) && (
                <p className="text-muted small">
                  Only the project creator can set status to Completed. Use &quot;Mark as Completed&quot; to request completion.
                </p>
              )}
              <div className="form-group mb-3">
                <label className="text-label"><strong>New Status*</strong></label>
                <select
                  className="form-control"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  disabled={statusLoading}
                >
                  <option value="">Select Status</option>
                  {getProjectStatusOptions({ isCreator: isProjectCreator(selectedProject) }).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="status-modal-footer">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => setShowStatusModal(false)}
              disabled={statusLoading}
              style={{ minWidth: '7rem', height: '38px' }}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary sw-btn-next" 
              type="button"
              onClick={handleStatusChange}
              disabled={!newStatus || statusLoading}
              style={{ minWidth: '7rem', height: '38px' }}
            >
              {statusLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Updating...
                </>
              ) : (
                'Update Status'
              )}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
      
      <BackToTop />
      <InternalFooter />
    </div>
  )
}
export default ProjectList;